import {
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
  computed
} from "@angular/core";
import { toObservable, toSignal } from "@angular/core/rxjs-interop";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { Store } from "@ngrx/store";
import {
  selectNfePresets,
  selectPNCompany
} from "@state/selectors/user.selectors";
import codigoIdentificadorDestino from "@static/cid.json";
import finalidadeOptions from "@static/finalidade.json";
import naturezaOptions from "@static/natureza.json";
import tipoEmissaoOptions from "@static/tipoEmissao.json";
import tipoImpressaoOptions from "@static/tipoImpressao.json";
import yesOrNotOptions from "@static/yesOrNot.json";
import { DropdownChangeEvent } from "primeng/dropdown";
import {
  FINALIDADE_DEVOLUCAO,
  chaveNfeReferenciadaValidator,
  futureDateValidator
} from "src/app/utils/nfe.validators";
import * as moment from "moment-timezone";
import { Subscription, skip } from "rxjs";

@Component({
  selector: "app-nfe-form",
  templateUrl: "./nfe-form.component.html",
  styleUrls: ["./nfe-form.component.scss"]
})
export class NfeFormComponent implements OnInit, OnDestroy {
  @Output() data = new EventEmitter();
  @Output() status = new EventEmitter();

  company = this.store.selectSignal(selectPNCompany);
  presets = this.store.selectSignal(selectNfePresets);

  series = computed(() =>
    (
      this.company()?.nfe?.config?.numeracao?.map((n) => ({
        display: n.serie,
        value: n.serie.toString()
      })) || []
    ).sort((a, b) =>
      a.value.toString().localeCompare(b.value.toString(), "pt-BR", {
        numeric: true,
        sensitivity: "base"
      })
    )
  );

  numeracaoAutomatica = computed(() => {
    return !!this.company()?.nfe?.config?.numeracaoAutomatica;
  });

  nfeForm = new FormGroup({
    numero: new FormControl("1", []),
    serie: new FormControl("1", []),
    finalidade: new FormControl(this.presets()?.finalidade, [
      Validators.required
    ]),
    natureza: new FormControl(this.presets()?.natureza, [Validators.required]),
    saida: new FormControl(this.presets()?.saida, [Validators.required]),
    presencial: new FormControl(this.presets()?.presencial, [
      Validators.required
    ]),
    tipoImpressao: new FormControl(this.presets()?.tipoImpressao, [
      Validators.required
    ]),
    tipoEmissao: new FormControl(this.presets()?.tipoEmissao, [
      Validators.required
    ]),
    informacoesComplementares: new FormControl(
      this.presets()?.informacoesComplementares
    ),
    codigoIdentificacaoDestino: new FormControl(
      this.presets()?.codigoIdentificacaoDestino,
      [Validators.required]
    ),
    consumidorFinal: new FormControl(this.presets()?.consumidorFinal, [
      Validators.required
    ]),
    dataSaidaEntrada: new FormControl(
      moment
        .tz(new Date(), "America/Sao_Paulo")
        .add(10, "minutes")
        .format("DD/MM/YYYY HH:mm"),
      [Validators.required, futureDateValidator]
    ),
    // Chave de acesso da NF-e original. Só é exigida (e exibida) quando a
    // finalidade é "4" (devolução); o validador lê a finalidade pelo parent.
    chaveNfeReferenciada: new FormControl<string | null>(null, [
      chaveNfeReferenciadaValidator
    ])
  });

  serie = toSignal(this.nfeForm.controls.serie.valueChanges, {
    initialValue: this.nfeForm.controls.serie.value
  });

  finalidadeAtual = toSignal(this.nfeForm.controls.finalidade.valueChanges, {
    initialValue: this.nfeForm.controls.finalidade.value
  });

  isDevolucao = computed(
    () => String(this.finalidadeAtual() ?? "").trim() === FINALIDADE_DEVOLUCAO
  );

  series$ = toObservable(this.series)
    .pipe(skip(0))
    .subscribe((series) => {
      const serieControl = this.nfeForm.controls.serie;
      const currentValue = (serieControl.value ?? "").toString().trim();
      const firstSerie = series[0]?.value?.toString() || "1";
      const hasMatchingOption = series.some(
        (option) => option.value?.toString() === currentValue
      );

      if (!currentValue) {
        serieControl.setValue(firstSerie);
        return;
      }

      if (!hasMatchingOption && !serieControl.dirty) {
        serieControl.setValue(firstSerie);
      }
    });

  numeracao = computed(() =>
    this.company()?.nfe?.config?.numeracao?.find(
      (s) =>
        s.serie?.toString().trim() === (this.serie() ?? "").toString().trim()
    )
  );

  numeracao$ = toObservable(this.numeracao)
    .pipe(skip(0))
    .subscribe((numeracao) => {
      const proximoNumero =
        numeracao?.numeracaoAtual ?? numeracao?.numero ?? "1";

      this.nfeForm.get("numero")?.setValue(proximoNumero.toString());
    });

  numeracaoAutomatica$ = toObservable(this.numeracaoAutomatica)
    .pipe(skip(0))
    .subscribe(() => {
      this.applyNumeracaoValidation();
    });

  finalidade = finalidadeOptions;
  tipoImpressao = tipoImpressaoOptions;
  tipoEmissao = tipoEmissaoOptions;
  codigoIdentificadorDestino = codigoIdentificadorDestino;
  natureza = naturezaOptions;
  yesOrNot = yesOrNotOptions;

  $: Subscription[] = [];

  constructor(private store: Store) {}

  ngOnInit(): void {
    this.handleFormChange();
    this.validPipeInfoComplementar();
    this.applyNumeracaoValidation();
    this.syncChaveReferenciadaWithFinalidade();
    this.data.emit(this.nfeForm.value);
    this.status.emit(this.nfeForm.status === "VALID");
  }

  /**
   * A obrigatoriedade da chave depende da finalidade, então a validação do
   * campo precisa ser reavaliada sempre que a finalidade mudar. Ao sair de
   * "devolução" o valor é limpo para não vazar `notaReferenciada` numa NF-e
   * normal.
   */
  private syncChaveReferenciadaWithFinalidade() {
    const finalidadeControl = this.nfeForm.controls.finalidade;
    const chaveControl = this.nfeForm.controls.chaveNfeReferenciada;

    this.$.push(
      finalidadeControl.valueChanges.subscribe((finalidade) => {
        const isDevolucao =
          String(finalidade ?? "").trim() === FINALIDADE_DEVOLUCAO;

        if (!isDevolucao && chaveControl.value) {
          chaveControl.setValue(null, { emitEvent: false });
        }

        chaveControl.updateValueAndValidity();
      })
    );

    chaveControl.updateValueAndValidity({ emitEvent: false });
  }

  applyNumeracaoValidation() {
    const isAutomatica = this.numeracaoAutomatica();
    const numeroControl = this.nfeForm.get("numero");
    const serieControl = this.nfeForm.get("serie");

    if (!isAutomatica) {
      numeroControl?.setValidators([Validators.required]);
      serieControl?.setValidators([Validators.required]);
    } else {
      numeroControl?.clearValidators();
      serieControl?.clearValidators();
    }

    numeroControl?.updateValueAndValidity({ emitEvent: false });
    serieControl?.updateValueAndValidity({ emitEvent: false });
  }

  validPipeInfoComplementar() {
    const infoComplementarControl = this.nfeForm.get(
      "informacoesComplementares"
    );

    if (!infoComplementarControl) return;

    this.$.push(
      infoComplementarControl.valueChanges.subscribe((value) => {
        if (typeof value === "string") {
          const sanitizedValue = value.replace(/\n/g, "|");
          if (sanitizedValue !== value) {
            infoComplementarControl.setValue(sanitizedValue, {
              emitEvent: false
            });
          }
        }
      })
    );
  }

  ngOnDestroy(): void {
    this.$.forEach(($) => $.unsubscribe());

    this.series$.unsubscribe();
    this.numeracao$.unsubscribe();
    this.numeracaoAutomatica$.unsubscribe();
  }

  handleFormChange() {
    this.$.push(
      this.nfeForm.valueChanges.subscribe((values) => {
        this.data.emit(values);
      }),
      this.nfeForm.statusChanges.subscribe((status) => {
        this.status.emit(status === "VALID");
      })
    );
  }

  onSerieChange(event: DropdownChangeEvent) {
    const serie = event?.value?.toString().trim();

    if (!serie) return;

    this.nfeForm.controls.serie.setValue(serie);
  }
}
